import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toSnake } from './utils';

function pyType(t: string): string {
  return t === 'number' ? 'float' : t === 'boolean' ? 'bool' : t === 'array' ? 'list' : t === 'object' ? 'dict' : 'str';
}

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `    '${a.key}': {'from_email': '${a.fromEmail}', 'key_env': '${a.envVarName}'}`)
    .join(',\n');

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const doc = t.schema
          ? `  # payload: ${t.schema.map((f) => f.key + (f.required ? '' : '?')).join(', ')}\n`
          : '';
        return `${doc}    '${t.camelName}': {'app': '${a.key}', 'slug': '${t.slug}'}`;
      }),
    )
    .join(',\n\n');

  return `# mail_config.py — generated by Mail Service SDK Export

MAIL_APPS = {
${appEntries},
}

MAIL_TEMPLATES = {
${templateEntries},
}
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const fnName = `send_${toSnake(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const params = t.schema
            .map((f) => `${f.key}: ${pyType(f.type)}${f.required ? '' : ' = None'}`)
            .join(', ');
          const dataDict = t.schema
            .map((f) => `'${f.key}': ${f.key}`)
            .join(', ');
          return `    def ${fnName}(self, recipient: str, ${params}) -> dict:\n        return self.send('${t.camelName}', recipient, {${dataDict}})`;
        }
        return `    def ${fnName}(self, recipient: str, data: dict = None) -> dict:\n        return self.send('${t.camelName}', recipient, data or {})`;
      }),
    )
    .join('\n\n');

  return `# mail_service.py — generated by Mail Service SDK Export
import os
import json
import urllib.request
from mail_config import MAIL_APPS, MAIL_TEMPLATES


class MailService:
    def __init__(self):
        self.service_url = os.environ.get('${config.serviceUrlVarName}', '').rstrip('/')

    def _get_key(self, app: str) -> str:
        return os.environ.get(MAIL_APPS[app]['key_env'], '')

    def _headers(self, app: str) -> dict:
        return {'Content-Type': 'application/json', 'X-API-KEY': self._get_key(app)}

    def _is_ready(self, app: str) -> bool:
        return bool(self.service_url and self._get_key(app))

    def _post(self, app: str, path: str, body: dict) -> dict:
        if not self._is_ready(app):
            print(f'[Mail] App "{app}" not configured')
            return {'success': False, 'error': 'Not configured'}
        try:
            data = json.dumps(body).encode('utf-8')
            req = urllib.request.Request(
                f'{self.service_url}{path}',
                data=data,
                headers=self._headers(app),
                method='POST',
            )
            with urllib.request.urlopen(req) as resp:
                return json.loads(resp.read())
        except Exception as exc:
            return {'success': False, 'error': str(exc)}

    def send(self, template: str, recipient: str, data: dict = None) -> dict:
        t = MAIL_TEMPLATES[template]
        return self._post(t['app'], '/v1/send', {
            'template_slug': t['slug'],
            'recipient': recipient,
            'data': data or {},
        })

    def send_raw(self, app: str, subject: str, html: str, recipient: str, from_name: str = None) -> dict:
        return self._post(app, '/v1/send/raw', {
            'subject': subject, 'html': html, 'recipient': recipient, 'from_name': from_name,
        })

${typedMethods}


mail = MailService()
`;
}

export const pythonGenerator: LanguageGenerator = {
  id: 'python',
  name: 'Python',
  category: 'full-sdk',
  color: '#3776AB',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'mail_config.py', content: genConfig(config), syntax: 'python' },
      { filename: 'mail_service.py', content: genService(config), syntax: 'python' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
